import { createSelector } from "reselect";
import { AppState } from "../appState";
import { IUserPermissions } from "../../models";

const userPermissionsSelector = (state: AppState) => state.userPermissions as unknown as (IUserPermissions | Record<string, any> | undefined);

export const hasAnyEnabledFor = (node: unknown): boolean => {
  if (typeof node === "boolean") return node;

  const enabledFor = (node as { enabledFor?: unknown } | null | undefined)?.enabledFor;
  // Preserve the existing fallback for missing permission metadata.
  if (!enabledFor) return true;
  if (!Array.isArray(enabledFor) || enabledFor.length === 0) return false;

  return true;
};

/**
 * These selectors must return booleans (used by ProtectedRoute and other UI conditionals).
 * Backward-compat: some tests still seed legacy boolean keys (e.g. userPermissions.onHandDelivery).
 */
export const hasOveragesPermissionSelector = createSelector(
  [userPermissionsSelector],
  (permissions) => hasAnyEnabledFor(permissions?.overages?.details)
);

export const hasShortagesPermissionSelector = createSelector(
  [userPermissionsSelector],
  (permissions) => hasAnyEnabledFor(permissions?.shortages?.details)
);

export const hasDamagesPermissionSelector = createSelector(
  [userPermissionsSelector],
  (permissions) => hasAnyEnabledFor(permissions?.damages?.details)
);

export const hasOnHandDeliveryPermissionSelector = createSelector(
  [userPermissionsSelector],
  (permissions) =>
    hasAnyEnabledFor(permissions?.onHandDeliveries?.details)
);

export const hasDamagesFollowUpCompletionPermissionSelector = createSelector(
  [userPermissionsSelector],
  (permissions) =>
    hasAnyEnabledFor(permissions?.damages?.followUpCompletion)
);

export const hasDamagesFollowUpCompletionPermissionEnabledForSelector = createSelector(
  [userPermissionsSelector],
  (permissions) => permissions?.damages?.followUpCompletion?.enabledFor ?? []
);

export const hasMisdeliveryPermissionSelector = createSelector(
  [userPermissionsSelector],
  (permissions) =>
    hasAnyEnabledFor(permissions?.misDeliveries?.details)
);

export const hasCompleteMisdeliveryPermissionSelector = createSelector(
  [userPermissionsSelector],
  (permissions) => permissions?.misDeliveries?.investigationCompletion?.enabledFor ?? []
);

export const hasMisdeliveryInvestigationPermissionSelector = createSelector(
  [userPermissionsSelector],
  (permissions) =>  permissions?.misDeliveries?.investigation?.enabledFor ?? []
);

export const hasMisdeliveryAuditPermissionSelector = createSelector(
  [userPermissionsSelector],
  (permissions) =>  permissions?.misDeliveries?.audit?.enabledFor ?? []
);

export const hasMisdeliveryHazmatApprovalPermissionSelector = createSelector(
  [userPermissionsSelector],
  (permissions) =>
    hasAnyEnabledFor(permissions?.misDeliveries?.bol?.hazmatApproval)
);

export const hasCompleteShortagesPermissionSelector = createSelector(
  [userPermissionsSelector],
  (permissions) =>
    hasAnyEnabledFor(
      permissions?.shortages?.investigationCompletion
    )
);

export const hasCompleteShortagesPermissionEnabledForSelector = createSelector(
  [userPermissionsSelector],
  (permissions) =>
    permissions?.shortages?.investigationCompletion?.enabledFor ?? []
);

export const hasCompleteOnHandPermissionSelector = createSelector(
  [userPermissionsSelector],
  (permissions) =>
      permissions?.onHandDeliveries?.investigationCompletion?.enabledFor ?? []
);

export const hasCompleteOnHandFollowUpSelector = createSelector(
  [userPermissionsSelector],
  (permissions) =>
      (permissions)?.onHandDeliveries?.followUpCompletion?.enabledFor ?? []
);

export const hasOhInvestigationSelector = createSelector(
  [userPermissionsSelector],
  (permissions) =>
    hasAnyEnabledFor(permissions?.onHandDeliveries?.investigation)
);
export const hasOhFollowUpSelector = createSelector(
  [userPermissionsSelector],
  (permissions) =>
    hasAnyEnabledFor(permissions?.onHandDeliveries?.followUp)
);

export const hasClearedPermissionSelector = createSelector(
  [userPermissionsSelector],
  (permissions) => hasAnyEnabledFor((permissions as any)?.cleared?.details)
);
